import cn from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import useIsomorphicLayoutEffect from '~utils/hooks/useIsomorphicLayoutEffect';
import usePurchase from '~utils/hooks/usePurchase';
import useResponsive from '~utils/hooks/useResponsive';

import { PickedSeat, SeatArea, SeatRow, SessionSeatsResponse } from '~types';

import Seat from './Seat';
import s from './Seats.module.css';

const SEAT_SIZE = 60;

type RowData = {
  isCouch: boolean;
  columns: number;
  areaNumber: number;
  areaCategory: string;
};

export interface SeatsProps {
  data?: SessionSeatsResponse;
  roomType?: { mega: boolean; comfort: boolean; premiere: boolean };
  numberSize?: 'normal' | 'small';
  onAddSeat: (seat: PickedSeat) => void;
  onRemoveSeat: (seatId: string) => void;
}

export const normalizeCouchSeats = (seats: SeatRow['seats']) => {
  const grouped = seats.reduce(
    (r, c) => {
      return {
        ...r,
        [c.seatNumber]: typeof r[c.seatNumber] === 'undefined' ? [c] : [...r[c.seatNumber], c],
      };
    },
    {} as Record<string, SeatRow['seats']>,
  );
  return Object.keys(grouped)
    .map((seatNumber) => {
      return {
        ...grouped[seatNumber][0],
        groupedGridSeatNumber: grouped[seatNumber].map((i) => i.gridSeatNumber),
      };
    })
    .sort((a, b) => parseInt(b.seatNumber) - parseInt(a.seatNumber));
};

const Seats: FC<SeatsProps> = ({ data, onAddSeat, onRemoveSeat, roomType }) => {
  const { type } = useResponsive();
  const { seats, reservedSeats } = usePurchase();
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const layoutRef = useRef<HTMLDivElement>(null);
  const zoomingRef = useRef<ReactZoomPanPinchRef>(null);
  const selectedSeatsIds = seats.map((seat) => seat.id);
  const reservedSeatsIds = reservedSeats?.map((seat) => seat.id) || [];
  const layoutContent = useMemo(() => {
    if (!data || containerWidth === 0) {
      return {
        gridColumns: 0,
        layoutWidth: 0,
        scaleRatio: 1,
        layoutHeight: 0,
      };
    }

    let maxRows = 0;
    let maxColumns = 0;
    data.areas.forEach((area) => {
      const hasCots = area.areaDescription.toLowerCase().includes('camastros');
      const rows = parseInt(area.areaLayoutRows, 10) * (hasCots ? 2 : 1);
      const columns = parseInt(area.areaLayoutColumns, 10);

      maxRows += rows;
      maxColumns = maxColumns < columns ? columns : maxColumns;
    });

    let scaleRatio = 1;
    /*const containerWidth = layoutRef?.current?.clientWidth ?? 0;
    console.log(containerWidth);*/
    const layoutWidth = (maxColumns + 2) * SEAT_SIZE;
    const layoutHeight = (maxRows + 2) * SEAT_SIZE;

    if (containerWidth !== 0) {
      if (containerWidth < layoutWidth) {
        scaleRatio = containerWidth / layoutWidth;
      }
    }

    return {
      scaleRatio,
      layoutWidth,
      gridColumns: maxColumns + 2,
      layoutHeight: layoutHeight * scaleRatio,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, type, containerWidth]);

  useEffect(() => {
    if (zoomingRef.current) {
      zoomingRef.current.resetTransform(0);
    }
  }, [data, type]);

  useIsomorphicLayoutEffect(() => {
    if (layoutRef?.current) {
      setContainerWidth(layoutRef.current?.offsetWidth);
    }
  }, []);

  const renderRow = (row: SeatRow, rowData: RowData) => {
    const { gridColumns } = layoutContent;
    const seats = rowData.isCouch ? normalizeCouchSeats(row.seats) : row.seats;
    return (
      // @ts-ignore
      <div className={s.layoutRow} style={{ '--gridColumns': gridColumns }} key={row.rowPhysicalId}>
        <span className={s.rowIndicator} style={{ textAlign: 'left', gridColumnStart: 1 }}>
          <span>{row.rowPhysicalId}</span>
        </span>
        {seats.map((seat) => {
          const seatKey = `${row.rowPhysicalId}${seat.seatNumber}`;
          const status = ((): '0' | '1' | '3' | '4' | '5' => {
            if (selectedSeatsIds.includes(seatKey)) return '5';
            if (reservedSeatsIds.includes(seatKey)) return '0';
            return seat.seatStatus as '1' | '3' | '4';
          })();

          const gridPosition = gridColumns - parseInt(seat.gridSeatNumber, 16);
          return (
            <Seat
              key={`seat-${seatKey}`}
              physicalPosition={seatKey}
              data={seat}
              status={status}
              gridPosition={gridPosition}
              isCouch={rowData.isCouch}
              onClick={(action) => {
                const { seatNumber, gridSeatNumber, groupedGridSeatNumber } = seat;
                const { rowPhysicalId, seatGridRowId } = row;
                const { areaNumber, areaCategory } = rowData;
                const pickedSeat = {
                  id: seatKey,
                  areaNumber,
                  areaCategory,
                  rowPhysicalId,
                  gridSeatNumber,
                  groupedGridSeatNumber,
                  rowId: seatGridRowId,
                  seatNumber: Number(seatNumber),
                };

                if (action === 'add') {
                  onAddSeat(pickedSeat);
                } else {
                  onRemoveSeat(seatKey);
                }
              }}
            />
          );
        })}
        <span
          className={s.rowIndicator}
          style={{ textAlign: 'right', gridColumnStart: gridColumns }}
        >
          <span>{row.rowPhysicalId}</span>
        </span>
      </div>
    );
  };

  const renderArea = (area: SeatArea) => {
    const layoutRows = parseInt(area.areaLayoutRows, 10);
    const layoutColumns = parseInt(area.areaLayoutColumns, 10);
    const isCouch = area.areaDescription.toLowerCase().includes('camastros');
    const rows = [];

    for (let i = layoutRows; i >= 0; i--) {
      const row = area.rows[i];

      if (row && row.rowPhysicalId) {
        rows.push(
          renderRow(row, {
            isCouch,
            columns: layoutColumns,
            areaCategory: area.areaCategory,
            areaNumber: Number(area.areaNumber),
          }),
        );
      }
    }

    return rows;
  };

  const areas = data?.areas || [];
  const shouldScale = layoutContent.scaleRatio < 1;
  const shouldRender = containerWidth !== 0 && layoutContent.layoutHeight > 0;

  return (
    <div ref={layoutRef} className={s.root}>
      <div className={s.layoutBody}>
        {shouldRender && (
          <TransformWrapper
            ref={zoomingRef}
            maxScale={1.5}
            minPositionX={0}
            minScale={layoutContent.scaleRatio}
            initialScale={layoutContent.scaleRatio}
            initialPositionX={0}
            initialPositionY={-0.5}
            disabled={!shouldScale}
            wheel={{ disabled: true }}
            doubleClick={{ disabled: true }}
          >
            <TransformComponent
              wrapperStyle={{
                width: 'auto',
                height: layoutContent.layoutHeight,
              }}
              contentStyle={{
                width: shouldScale ? 'fit-content' : 'auto',
              }}
            >
              <div className={cn({ [s.layoutLargeNumber]: roomType?.mega })}>
                <div className={cn(s.layoutScreen, { [s.layoutMegaScreen]: roomType?.mega })}>
                  <span>{roomType?.mega ? 'Mega Pantalla' : 'Pantalla'}</span>
                </div>
                <div className={cn(s.layoutSeats)} style={{ width: layoutContent.layoutWidth }}>
                  {areas.map((area: SeatArea) => {
                    return renderArea(area);
                  })}
                </div>
              </div>
            </TransformComponent>
          </TransformWrapper>
        )}
      </div>
      <ul className={s.layoutLegend}>
        <li className={s.legendItem}>
          <span className={cn(s.legendIcon, s.occupied)} /> Ocupados
        </li>
        <li className={s.legendItem}>
          <span className={cn(s.legendIcon, s.available)} /> Disponibles
        </li>
        <li className={s.legendItem}>
          <span className={cn(s.legendIcon, s.selected)} /> Seleccionados
        </li>
        <li className={s.legendItem}>
          <span className={cn(s.legendIcon, s.unavailable)} />
          Distanciamiento
        </li>
      </ul>
    </div>
  );
};

export default Seats;
